import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import Reveal from "@/components/Reveal";

interface SectionProps {
  id?: string;
  eyebrow?: string;
  title?: ReactNode;
  titleAs?: "h1" | "h2";
  description?: string;
  align?: "center" | "left";
  surface?: "default" | "alt";
  glow?: boolean;
  grid?: boolean;
  containerSize?: "default" | "wide" | "narrow";
  className?: string;
  headerClassName?: string;
  children: ReactNode;
}

/**
 * Shared foundation for every inner page section.
 * - Consistent vertical rhythm (py-24)
 * - Optional eyebrow + gradient-accented title + lead description
 * - Alternating surface backgrounds for natural separation
 * - Optional radial glow + subtle dot grid for depth
 */
const Section = ({
  id,
  eyebrow,
  title,
  titleAs = "h2",
  description,
  align = "center",
  surface = "default",
  glow = true,
  grid = false,
  containerSize = "default",
  className,
  headerClassName,
  children,
}: SectionProps) => {
  const TitleTag = titleAs;
  const surfaceClass = surface === "alt" ? "bg-surface-1" : "bg-background";
  const containerMax =
    containerSize === "wide"
      ? "max-w-7xl"
      : containerSize === "narrow"
      ? "max-w-4xl"
      : "max-w-6xl lg:max-w-7xl";
  const alignClass = align === "center" ? "text-center mx-auto" : "text-left";
  const headingMax = align === "center" ? "max-w-3xl mx-auto" : "max-w-2xl";
  const accentBarPos = align === "center" ? "mx-auto" : "";

  const hasHeader = Boolean(eyebrow || title || description);

  return (
    <section
      id={id}
      className={cn(
        "relative py-10 sm:py-14 md:py-24 px-4 sm:px-6 overflow-hidden",
        surfaceClass,
        className
      )}
    >
      {glow && (
        <div
          aria-hidden
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[60rem] h-[40rem] bg-gradient-radial-glow opacity-50 pointer-events-none"
        />
      )}
      {grid && (
        <div
          aria-hidden
          className="absolute inset-0 bg-grid-faint opacity-30 pointer-events-none"
        />
      )}

      <div className={cn("relative container mx-auto", containerMax)}>
        {hasHeader && (
          <Reveal className={cn("mb-8 md:mb-14", alignClass, headingMax, headerClassName)}>
            {eyebrow && (
              <p className="text-sm font-semibold tracking-widest text-primary uppercase mb-3">
                {eyebrow}
              </p>
            )}
            {title && (
              <TitleTag className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground mb-3 md:mb-4 leading-tight">
                {title}
              </TitleTag>
            )}
            <div
              className={cn(
                "h-1 w-20 rounded-full bg-gradient-brand mb-4 md:mb-6",
                accentBarPos
              )}
            />
            {description && (
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                {description}
              </p>
            )}
          </Reveal>
        )}

        {children}
      </div>
    </section>
  );
};

export default Section;
